/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    TRACING_SYSTEM
 * Date:         2025-12-20
 * Modified By:  Cursor
 *
 * Description:
 * Component to display trace status with progress indicator.
 */

import React from 'react';
import { useTraceStatus } from '../../hooks/useTraceStatus';
import { Trace } from '../../services/traces';

interface TraceStatusProps {
  traceId: string | null;
  showDetails?: boolean;
  onComplete?: (trace: Trace) => void;
  onFailed?: (trace: Trace) => void;
}

export function TraceStatus({ 
  traceId, 
  showDetails = false,
  onComplete,
  onFailed
}: TraceStatusProps) {
  const { trace, loading, error } = useTraceStatus(traceId, {
    onComplete,
    onFailed
  });

  if (!traceId) return null;

  if (loading && !trace) {
    return (
      <div className="alert alert-info">
        <div className="spinner-border spinner-border-sm me-2" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        Initializing...
      </div>
    );
  }

  if (error && !trace) {
    return (
      <div className="alert alert-danger">
        Error: {error}
      </div>
    );
  }

  if (!trace) return null;

  return (
    <div className={`alert alert-${getStatusColor(trace.status)}`}>
      <div className="d-flex align-items-center justify-content-between">
        <div>
          <strong>{trace.description}</strong>
          <div className="small mt-1">
            Status: <span className="badge bg-secondary">{trace.status}</span>
          </div>
        </div>

        {/* Progress Bar */}
        {trace.progress.total > 0 && (
          <div className="ms-3" style={{ minWidth: '200px' }}>
            <div className="progress" style={{ height: '20px' }}>
              <div
                className="progress-bar"
                role="progressbar"
                style={{ width: `${trace.progress.percentage}%` }}
                aria-valuenow={trace.progress.percentage}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                {trace.progress.percentage}%
              </div>
            </div>
            <div className="small text-muted mt-1">
              {trace.progress.current} / {trace.progress.total}
            </div>
          </div>
        )}
      </div>

      {/* Error Details */}
      {trace.status === 'FAILED' && trace.error && (
        <div className="mt-2">
          <strong>Error:</strong> {trace.error.message}
        </div>
      )}

      {/* Success Details */}
      {trace.status === 'SUCCESS' && trace.result && showDetails && (
        <div className="mt-2">
          <pre className="mb-0">
            {JSON.stringify(trace.result, null, 2)}
          </pre>
        </div>
      )}

      {/* Event Timeline */}
      {showDetails && trace.events && trace.events.length > 0 && (
        <div className="mt-3">
          <strong>Timeline:</strong>
          <ul className="list-unstyled mt-2 mb-0">
            {trace.events.map((event, idx) => (
              <li key={idx} className="small">
                <code>{new Date(event.timestamp).toLocaleTimeString()}</code>{' '}
                <span className="badge bg-secondary">{event.service}</span>{' '}
                {event.message}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function getStatusColor(status: string): string {
  switch (status) {
    case 'SUCCESS':
      return 'success';
    case 'FAILED':
      return 'danger';
    case 'PROCESSING':
      return 'info';
    case 'PENDING':
      return 'warning';
    default:
      return 'secondary';
  }
}

